import { useSelector } from "react-redux";

import { selectCurrentAccount, selectUserId } from "fond/api";
import { RECENT_PROJECTS_KEY } from "fond/constants";
import { RecentlyViewed, RecentlyViewedEntity, ResourceEntity } from "fond/types";
import { getItem } from "fond/utils/localStorage";

import { useContents } from "./useContents";

const isRecentEntityType = (item: ResourceEntity): item is ResourceEntity =>
  item.EntityType === "project" || item.EntityType === "report" || (item.EntityType === "folder" && item.MultiProject != null);

const wasRecentlyAccessed =
  (recentItems: RecentlyViewed[]) =>
  (item: ResourceEntity): item is ResourceEntity =>
    isRecentEntityType(item) && recentItems.some((recent) => recent.projectId === item.ID);

export const useRecentItems = (): RecentlyViewedEntity<ResourceEntity>[] => {
  const { items } = useContents();
  const userId = useSelector(selectUserId);
  const accountId = useSelector(selectCurrentAccount)?.ID;

  // Load recent projects from local storage
  let recentItemsFromStorage: RecentlyViewed[] = getItem(`${RECENT_PROJECTS_KEY}_${userId}_${accountId}`, []);

  // Get recently viewed project details
  const wasRecentlyAccessedFilter = wasRecentlyAccessed(recentItemsFromStorage);
  const recentItems: RecentlyViewedEntity<ResourceEntity>[] = items
    .filter(wasRecentlyAccessedFilter)
    .map((recentItem) => ({
      ...recentItem,
      LastViewed: recentItemsFromStorage.find((recent) => recent.projectId === recentItem.ID)?.lastViewed,
    }))
    .sort((a, b) => {
      return new Date(b.LastViewed ?? 0).getTime() - new Date(a.LastViewed ?? 0).getTime();
    });

  return recentItems;
};
